import type { MedplumClient } from "@medplum/core";
import { triggerPracticeAppointmentTypeChildEventMappingsSync } from "@/background/practice/on-practice-appointment-type-child-event-mappings-sync";
import { CalClient, type CalTeamClient } from "@/lib/cal/client";
import { CalApiError } from "@/lib/cal/types";
import { createLogger } from "@/lib/logger";
import { Practice } from "@/models/practice/Practice";
import { Provider } from "@/models/provider/Provider";
import { ProviderRole } from "@/models/provider/ProviderRole";

const logger = createLogger("data/scheduling/add-provider-to-team");

export interface AddProviderToTeamInput {
  medplum: MedplumClient;
  providerId: string;
  practiceId: string;
  role?: "MEMBER" | "ADMIN" | "OWNER";
  actorProviderId?: string;
  /**
   * The Practitioner named by `providerId`, when the caller already holds a current copy.
   * Pass it right after writing the provider's Cal linkage: a Medplum search issued within
   * milliseconds of that write can still return the old version, which reads as "no Cal user".
   */
  provider?: Provider;
}

export interface AddProviderToTeamResult {
  success: boolean;
  membershipId?: number;
  error?: string;
  /** The provider carrying the team membership identifiers. Set on success. */
  provider?: Provider;
}

/**
 * Adds a provider to a practice's Cal.com team.
 * This helper is intended for trusted server-side callers only.
 */
export async function addProviderToTeam({
  medplum,
  providerId,
  practiceId,
  role,
  actorProviderId,
  provider: currentProvider,
}: AddProviderToTeamInput): Promise<AddProviderToTeamResult> {
  try {
    const practice = await Practice.getById(medplum, practiceId);
    if (!practice.calTeamId) {
      return {
        success: false,
        error: "Practice does not have a Cal.com team configured",
      };
    }
    const calTeamIdRaw = practice.calTeamId.trim();
    if (!/^\d+$/.test(calTeamIdRaw)) {
      return {
        success: false,
        error: `Practice has invalid Cal.com team ID "${practice.calTeamId}"`,
      };
    }
    const teamId = Number.parseInt(calTeamIdRaw, 10);

    const provider = currentProvider ?? (await Provider.getById(medplum, providerId, false));
    const calUserId = provider.calUserId;
    if (calUserId == null) {
      return {
        success: false,
        error: "Provider does not have a Cal.com user ID",
      };
    }

    // Scope the role lookup to this practice - provider.providerRole is whichever role loaded
    // first, which is arbitrary for a provider in more than one practice.
    const practiceRole = await ProviderRole.searchOne(medplum, {
      practitioner: provider.referenceObject,
      organization: practice.referenceObject,
    });
    if (!practiceRole) {
      logger.error("Security: Attempted to add provider to practice they don't belong to", {
        actorProviderId,
        providerId,
        practiceId,
      });
      return {
        success: false,
        error: "Provider does not belong to this practice",
      };
    }

    if (provider.calTeamMembershipId && provider.calTeamId) {
      const teamIdMatch = provider.calTeamId === teamId;

      logger.info("Provider already has Cal.com team membership", {
        actorProviderId,
        providerId,
        practiceId,
        existingTeamId: provider.calTeamId,
        existingMembershipId: provider.calTeamMembershipId,
        requestedTeamId: practice.calTeamId,
        teamIdMatch,
      });

      if (teamIdMatch) {
        // Keep child mappings fresh even when membership already exists.
        try {
          await triggerPracticeAppointmentTypeChildEventMappingsSync({
            practiceId: practice.id,
            teamId,
            reason: "provider-added",
            actorProviderId,
            calUserId,
          });
        } catch (error) {
          logger.warn("Failed to queue child event mapping sync for existing team membership", {
            actorProviderId,
            providerId,
            practiceId,
            teamId,
            error: error instanceof Error ? error.message : String(error),
          });
        }

        return {
          success: true,
          membershipId: provider.calTeamMembershipId,
          provider,
        };
      }

      logger.warn("Provider has membership to different team", {
        actorProviderId,
        providerId,
        practiceId,
        existingTeamId: provider.calTeamId,
        requestedTeamId: practice.calTeamId,
      });
    }

    const response = await createTeamMembershipWithReadBack({
      calOrgClient: CalClient.forTeam(),
      teamId,
      calUserId,
      role: role || "ADMIN",
      logContext: { actorProviderId, providerId, practiceId },
    });

    if (response.status !== "success" || !response.data) {
      logger.error("Failed to add provider to Cal.com team", {
        actorProviderId,
        providerId,
        practiceId,
        calTeamId: practice.calTeamId,
        calUserId,
      });
      return { success: false, error: "Failed to add provider to team" };
    }

    const updatedProvider = await Provider.patchWithQuery(medplum, providerId, {
      calTeamId: response.data.teamId,
      calTeamMembershipId: response.data.id,
    });

    logger.info("Provider added to Cal.com team successfully", {
      actorProviderId,
      providerId,
      practiceId,
      calTeamId: practice.calTeamId,
      calUserId,
      membershipId: response.data.id,
      role: response.data.role,
    });

    try {
      await triggerPracticeAppointmentTypeChildEventMappingsSync({
        practiceId: practice.id,
        teamId,
        reason: "provider-added",
        actorProviderId,
        calUserId,
      });
    } catch (error) {
      logger.warn("Failed to queue child event mapping sync after adding provider to team", {
        actorProviderId,
        providerId,
        practiceId,
        teamId,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    return { success: true, membershipId: response.data.id, provider: updatedProvider };
  } catch (error) {
    logger.error("Error adding provider to Cal.com team", {
      actorProviderId,
      providerId,
      practiceId,
      error,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

// ============================================================================
// Membership create with read-back
// ============================================================================

type CreateTeamMembershipResponse = Awaited<ReturnType<CalTeamClient["createTeamMembership"]>>;

interface CreateTeamMembershipWithReadBackInput {
  calOrgClient: CalTeamClient;
  teamId: number;
  calUserId: number;
  role: "MEMBER" | "ADMIN" | "OWNER";
  logContext: { actorProviderId?: string; providerId: string; practiceId: string };
}

/**
 * Creates the team membership. On a Cal 5xx, reads the team memberships back before a retry.
 *
 * The shared Cal fetch does not replay a POST: a 5xx (a Cloudflare 524 in practice) does not tell
 * whether Cal applied the write. The read-back decides. If the user is already a member, that
 * membership is the result. Otherwise the POST is sent once more.
 */
async function createTeamMembershipWithReadBack({
  calOrgClient,
  teamId,
  calUserId,
  role,
  logContext,
}: CreateTeamMembershipWithReadBackInput): Promise<CreateTeamMembershipResponse> {
  const createMembership = () =>
    calOrgClient.createTeamMembership({
      teamId,
      request: { userId: calUserId, accepted: true, role },
    });

  try {
    return await createMembership();
  } catch (error) {
    if (!(error instanceof CalApiError) || error.statusCode < 500) {
      throw error;
    }

    // a list failure throws to the caller - same outcome as the first 5xx
    const existing = await findTeamMembership({ calOrgClient, teamId, calUserId });
    if (existing) {
      logger.info("Cal.com team membership POST failed but the membership exists", {
        ...logContext,
        teamId,
        calUserId,
        membershipId: existing.id,
        statusCode: error.statusCode,
      });
      return { status: "success", data: existing };
    }

    logger.warn("Cal.com team membership POST failed with a 5xx, retrying once", {
      ...logContext,
      teamId,
      calUserId,
      statusCode: error.statusCode,
    });
    return createMembership();
  }
}

/** Cal's maximum page size for skip/take pagination. */
const MEMBERSHIP_PAGE_SIZE = 250;
/** Upper bound on pages read - stops a loop if Cal ignores `skip`. */
const MAX_MEMBERSHIP_PAGES = 20;

/**
 * Finds the user's membership across all pages of the team. A first-page-only read misses a
 * member on a large team, and the retry POST then creates a duplicate.
 */
async function findTeamMembership({
  calOrgClient,
  teamId,
  calUserId,
}: Pick<CreateTeamMembershipWithReadBackInput, "calOrgClient" | "teamId" | "calUserId">) {
  for (let pageIndex = 0; pageIndex < MAX_MEMBERSHIP_PAGES; pageIndex++) {
    const page = await calOrgClient.listTeamMemberships({
      teamId,
      take: MEMBERSHIP_PAGE_SIZE,
      skip: pageIndex * MEMBERSHIP_PAGE_SIZE,
    });
    if (page.status !== "success") {
      return undefined;
    }
    const memberships = page.data ?? [];
    const match = memberships.find((membership) => membership.userId === calUserId);
    if (match || memberships.length < MEMBERSHIP_PAGE_SIZE) {
      return match;
    }
  }
  return undefined;
}
